/**
 * Feature flag management utility
 * This can be extended to integrate with external feature flag services like LaunchDarkly, Split, etc.
 */

export interface FeatureFlag {
  key: string;
  enabled: boolean;
  description: string;
  defaultValue: boolean;
}

// Define all feature flags here
export const FEATURE_FLAGS: Record<string, FeatureFlag> = {
  plan_row_inline_edit_v1: {
    key: 'plan_row_inline_edit_v1',
    enabled: false, // Start disabled for controlled rollout
    description:
      'Enable inline editing of plan providers with improved UX',
    defaultValue: false,
  },
  // Add more feature flags here as needed
  enhanced_pagination: {
    key: 'enhanced_pagination',
    enabled: true,
    description: 'Enable enhanced pagination with page size options',
    defaultValue: false,
  },
  provider_insurance_hotfix_v1: {
    key: 'provider_insurance_hotfix_v1',
    enabled: true, // Enable provider insurance section in Edit Provider form
    description: 'Provider-scoped insurance editing hotfix',
    defaultValue: false,
  },
  plan_details_revamp_v0: {
    key: 'plan_details_revamp_v0',
    enabled: true, // Enable plan details page
    description: 'Enable plan details page functionality',
    defaultValue: false,
  },
};

class FeatureFlagManager {
  private flags: Record<string, boolean> = {};
  private initialized = false;

  constructor() {
    this.loadFlags();
  }

  /**
   * Load feature flags from localStorage and merge with defaults
   */
  private loadFlags() {
    try {
      const stored = localStorage.getItem('featureFlags');
      const storedFlags = stored ? JSON.parse(stored) : {};

      // Merge stored flags with default flags
      this.flags = Object.keys(FEATURE_FLAGS).reduce((acc, key) => {
        const flag = FEATURE_FLAGS[key];
        // Use stored value if available, otherwise use flag's enabled value
        acc[key] = storedFlags[key] !== undefined ? storedFlags[key] : flag.enabled;
        return acc;
      }, {} as Record<string, boolean>);

      this.initialized = true;
    } catch (error) {
      console.warn('Failed to load feature flags from localStorage:', error);
      // Fall back to default values
      this.flags = Object.keys(FEATURE_FLAGS).reduce((acc, key) => {
        acc[key] = FEATURE_FLAGS[key].enabled;
        return acc;
      }, {} as Record<string, boolean>);
      this.initialized = true;
    }
  }

  /**
   * Save current flags to localStorage
   */
  private saveFlags() {
    try {
      localStorage.setItem('featureFlags', JSON.stringify(this.flags));
    } catch (error) {
      console.warn('Failed to save feature flags to localStorage:', error);
    }
  }

  /**
   * Check if a feature flag is enabled
   */
  isEnabled(flagKey: string): boolean {
    if (!this.initialized) {
      this.loadFlags();
    }

    const flag = FEATURE_FLAGS[flagKey];
    if (!flag) {
      console.warn(`Feature flag "${flagKey}" not found, returning false`);
      return false;
    }

    return this.flags[flagKey] ?? flag.defaultValue;
  }

  /**
   * Enable a feature flag
   */
  enable(flagKey: string): void {
    if (!FEATURE_FLAGS[flagKey]) {
      console.warn(`Feature flag "${flagKey}" not found`);
      return;
    }

    this.flags[flagKey] = true;
    this.saveFlags();
  }

  /**
   * Disable a feature flag
   */
  disable(flagKey: string): void {
    if (!FEATURE_FLAGS[flagKey]) {
      console.warn(`Feature flag "${flagKey}" not found`);
      return;
    }

    this.flags[flagKey] = false;
    this.saveFlags();
  }

  /**
   * Toggle a feature flag
   */
  toggle(flagKey: string): boolean {
    const newValue = !this.isEnabled(flagKey);
    if (newValue) {
      this.enable(flagKey);
    } else {
      this.disable(flagKey);
    }
    return newValue;
  }

  /**
   * Get all feature flags with their current values
   */
  getAllFlags(): Record<string, { enabled: boolean; description: string }> {
    return Object.keys(FEATURE_FLAGS).reduce((acc, key) => {
      const flag = FEATURE_FLAGS[key];
      acc[key] = {
        enabled: this.isEnabled(key),
        description: flag.description,
      };
      return acc;
    }, {} as Record<string, { enabled: boolean; description: string }>);
  }

  /**
   * Reset all flags to their default values
   */
  resetToDefaults(): void {
    this.flags = Object.keys(FEATURE_FLAGS).reduce((acc, key) => {
      acc[key] = FEATURE_FLAGS[key].enabled;
      return acc;
    }, {} as Record<string, boolean>);
    this.saveFlags();
  }
}

// Export singleton instance
export const featureFlags = new FeatureFlagManager();

// Export React hook for easy usage in components
export const useFeatureFlag = (flagKey: string): boolean => {
  return featureFlags.isEnabled(flagKey);
};

// For development - expose feature flags on window for easy testing
if (import.meta.env.DEV) {
  (window as unknown as { featureFlags: typeof featureFlags }).featureFlags = featureFlags;
  // Console logging removed for production safety.
}
